// #336 slice 5: the round actions that follow the rules (brief §5.2.3,
// §5.4b). A round's type, a round taken out, four rounds added (flow 9):
// each slot's rule is read before the change and written again after it, so
// a deload round gets its effect and the progression resumes after it. The
// cells «a mano» stay, and move with their round. A slot with no rule is
// left as the structural action leaves it. A superset takes the slot's set
// count, as applyRule does (Q7). Keep free of React and any UI import.
import type { Draft } from "./programEdit.js";
import { MAX_CYCLE_WEEKS, setCycleWeeks } from "./programEditActions.js";
import { cloneWeekInDraft } from "./programEditWeekClone.js";
import { removeWeekAt } from "./programMatrixEdits.js";
import {
  defaultDeload,
  isEffortAxis,
  type GenerateOptions,
  type ProgressionRule,
  type RuleException,
} from "./programProgression.js";
import { applyRule, draftRoundTypes, recognizeWith, type SlotRecognizer } from "./programProgressionApply.js";
import { setRoundType } from "./programRoundTypes.js";
import type { RoundType } from "./schemas/programV3.js";

type RoundOptions = GenerateOptions & { lockedThrough?: number; recognize?: SlotRecognizer };

/** Every slot of `before` that follows a rule, written again into `after`
 *  from round `from` on. `mapWeek` takes a round of `before` to its place in
 *  `after` (null: gone), so an exception moves with its round. */
const rewrite = (
  before: Draft,
  after: Draft,
  mapWeek: (w: number) => number | null,
  from: number,
  opts: RoundOptions,
  pick: (rule: ProgressionRule) => ProgressionRule | null = (r) => r,
  keepHand = true,
): Draft => {
  const oldTypes = draftRoundTypes(before);
  let next = after;
  before.sessions.forEach((session, d) => {
    for (const slot of session.slots) {
      const rec = recognizeWith(slot, oldTypes, opts);
      if (rec.kind === "manual") continue;
      const rule = pick(rec.rule);
      const j = next.sessions[d]?.slots.findIndex((s) => s.id === slot.id) ?? -1;
      const target = next.sessions[d]?.slots[j];
      if (!rule || !target) continue;
      const keep: RuleException[] = keepHand
        ? rec.exceptions.flatMap((e) => {
            const w = mapWeek(e.week);
            return w === null ? [] : [{ ...e, week: w }];
          })
        : [];
      next = applyRule(next, { day: d, slot: j }, rule, { ...opts, from, keep });
    }
  });
  return next;
};

/** Round `week` given `type`, every rule following it («Applicato a: tutto il
 *  programma»). A rule with no deload of its own takes defaultDeload (§5.3). An
 *  executed round: the same draft. */
export const setRoundTypeByRules = (draft: Draft, week: number, type: RoundType, opts: RoundOptions = {}): Draft => {
  if (week <= (opts.lockedThrough ?? -1)) return draft;
  const typed = setRoundType(draft, week, type);
  if (typed === draft) return draft;
  const pick = (r: ProgressionRule) =>
    type === "deload" && r.deload === undefined ? { ...r, deload: defaultDeload(r.axis) } : r;
  return rewrite(draft, typed, (w) => w, week, opts, pick);
};

/** Round `week` taken out; the cells «a mano» after it move with their round,
 *  the rest follows the rule again. An executed round: the same draft. */
export const removeRoundByRules = (draft: Draft, week: number, opts: RoundOptions = {}): Draft => {
  if (week <= (opts.lockedThrough ?? -1)) return draft;
  const removed = removeWeekAt(draft, week);
  if (removed === draft) return draft;
  return rewrite(draft, removed, (w) => (w === week ? null : w > week ? w - 1 : w), week, opts);
};

export type NextBlockResult = { ok: true; draft: Draft } | { ok: false; reason: "full" };

/** A rule that continues from where it was in the next block: a load row, a
 *  threshold, or reps or seconds that move. Effort and % rows repeat the
 *  shape instead (§5.4b flow 9: the load rises with the measured MAX). */
const continues = (r: ProgressionRule): boolean =>
  r.axis === "absolute" || r.threshold !== undefined || (r.reps.kind !== "constant" && !isEffortAxis(r.axis));

/** Flow 9: `rounds` more rounds (default 4) after the last. Each copies the
 *  round `rounds` places before it (cloneWeekInDraft) with its type, or the
 *  type in `types`. A rule that continues is then written on from there.
 *  "full": the cycle would pass MAX_CYCLE_WEEKS. */
export const addNextBlock = (
  draft: Draft,
  opts: RoundOptions & { rounds?: number; types?: readonly RoundType[] } = {},
): NextBlockResult => {
  const n = draft.cycleWeeks;
  const k = opts.rounds ?? 4;
  if (k < 1 || n + k > MAX_CYCLE_WEEKS) return { ok: false, reason: "full" };
  const oldTypes = draftRoundTypes(draft);
  const source = (j: number) => (n >= k ? n - k + j : j % n);
  let next = setCycleWeeks(draft, n + k, "blank");
  for (let j = 0; j < k; j += 1) {
    const cloned = cloneWeekInDraft(next, source(j), [n + j]);
    if (cloned.ok) next = cloned.draft;
    next = setRoundType(next, n + j, opts.types?.[j] ?? oldTypes[source(j)] ?? "normal");
  }
  return { ok: true, draft: rewrite(draft, next, (w) => w, n, opts, (r) => (continues(r) ? r : null), false) };
};
